import { internalLogger } from '../../interface/v1/clientOsLogger';
import bindAllMethods from '../../utils/bindAllMethods';

import { SetServiceDependencies } from '../../infra/startup/types';
import { Refreshable } from '../../utils/refreshable/IRefreshable';
import { ClientOSError } from '../../infra/errors/ClientOSError';
import { ClientOSErrorCodes } from '../../infra/errors/ErrorCodes';

import IConsentService from './IConsentService';
import { IConsentStrategy } from './strategies/IConsentStrategy';
import { ConsentStrategyFactory } from './ConsentStrategyFactory';

export type ConsentServiceInputType = {
  isNative: boolean;
  isEmailGathering?: boolean;
  globalInjectConsentBanner?: boolean;
};

/**
 * Enhanced ConsentService that uses strategy pattern for web/native environments
 * Automatically selects appropriate strategy based on platform detection
 *
 * NOTE: This service extends Refreshable instead of InternalService to maintain
 * compatibility with the refresh mechanism. InternalService pattern is not applied here.
 */
export default class ConsentService
  extends Refreshable
  implements IConsentService
{
  private strategy: IConsentStrategy;

  constructor(consentServiceParams: ConsentServiceInputType) {
    super();

    this.strategy = ConsentStrategyFactory.createStrategy(consentServiceParams);

    this.setRefreshMethods({
      init: this.init.bind(this),
      refresh: this.refresh.bind(this)
    });

    bindAllMethods(this);
  }

  public setDependencies(dependencies: SetServiceDependencies): void {
    // Pass dependencies to the selected strategy
    this.strategy.setDependencies(dependencies);
  }

  public async init(): Promise<void> {
    try {
      await this.strategy.init();
      this._markAsReady();
    } catch (error) {
      internalLogger?.error('ConsentService: Initialization failed:', error);
      this._markAsReady();
      // CLIENTOS_OPP_002: propagate as structured error up to secondary critical path.
      if (error instanceof ClientOSError) throw error;
      throw new ClientOSError(
        'ConsentService: Initialization failed',
        ClientOSErrorCodes.CONSENT_INIT_FAILED,
        error
      );
    }
  }

  public async refresh(): Promise<void> {
    try {
      await this.strategy.refresh();
    } catch (error) {
      internalLogger?.error('ConsentService: Refresh failed:', error);
      // CLIENTOS_OPP_002: propagate as structured error up to secondary critical path.
      if (error instanceof ClientOSError) throw error;
      throw new ClientOSError(
        'ConsentService: Refresh failed',
        ClientOSErrorCodes.CONSENT_INIT_FAILED,
        error
      );
    }
  }

  public async updateOptInGroupsInValueStore(): Promise<void> {
    await this.strategy.updateOptInGroupsInValueStore();
  }
}

/**
 * Factory function to create ConsentService for web environment explicitly
 */
export function createWebConsentService(
  config: Omit<ConsentServiceInputType, 'isNative'> = {}
): ConsentService {
  return new ConsentService({
    ...config,
    isNative: false
  });
}

/**
 * Factory function to create ConsentService for native environment explicitly
 */
export function createNativeConsentService(
  config: Omit<ConsentServiceInputType, 'isNative'> = {}
): ConsentService {
  return new ConsentService({
    ...config,
    isNative: true
  });
}
